import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { eur, dateIt } from "@/lib/format";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ModalityBadge from "@/components/app/ModalityBadge";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Settings2, Minus, KeyRound } from "lucide-react";
import ResetPasswordDialog from "@/components/app/ResetPasswordDialog";

const empty = {
  company_name: "", vat_number: "", fiscal_code: "", address: "", contact_name: "", email: "", phone: "",
  pec: "", sdi_code: "", billing_mode: "slot", slots_low_threshold: 5, active: true, initial_slots: 0, password: "",
};
const TEXT_FIELDS = [
  ["company_name", "Ragione sociale *"], ["email", "Email (login) *"], ["vat_number", "Partita IVA"],
  ["fiscal_code", "Codice fiscale"], ["contact_name", "Referente"], ["phone", "Telefono"],
  ["address", "Indirizzo"], ["pec", "PEC"], ["sdi_code", "Codice SDI"],
];
const modeLabel = (m) => (m === "slot" ? "Slot prepagati" : "Fatturazione mensile");
const opLabel = { assegnazione: "Assegnazione iniziale", aggiunta: "Aggiunta", sottrazione: "Sottrazione", utilizzo: "Utilizzo iscrizione", restituzione: "Restituzione" };

function ClientFormDialog({ open, onClose, editing, onSaved }) {
  const [form, setForm] = useState(empty);
  useEffect(() => { if (open) setForm(editing ? { ...empty, ...editing, password: "" } : empty); }, [open, editing]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    if (!form.company_name.trim() || !form.email.trim()) return toast.error("Ragione sociale ed email obbligatorie");
    const body = { ...form, slots_low_threshold: parseInt(form.slots_low_threshold || 0, 10), initial_slots: parseInt(form.initial_slots || 0, 10), password: form.password || null };
    try {
      const { data } = editing ? await api.put(`/clients/${editing.id}`, body) : await api.post("/clients", body);
      toast.success(editing ? "Cliente aggiornato" : "Cliente creato");
      onSaved(editing ? null : data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="client-form-dialog">
        <DialogHeader><DialogTitle>{editing ? "Modifica cliente" : "Nuovo cliente"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {TEXT_FIELDS.map(([k, l]) => (
            <div key={k}><Label className="text-xs">{l}</Label><Input value={form[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`client-input-${k}`} /></div>
          ))}
          {!editing && <div><Label className="text-xs">Password iniziale (vuoto = generata)</Label><Input value={form.password} onChange={(e) => set("password", e.target.value)} data-testid="client-input-password" /></div>}
          <div><Label className="text-xs">Modalità operativa *</Label>
            <Select value={form.billing_mode} onValueChange={(v) => set("billing_mode", v)}>
              <SelectTrigger data-testid="client-billing-mode"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="slot">Slot prepagati</SelectItem><SelectItem value="mensile">Fatturazione mensile</SelectItem></SelectContent>
            </Select>
          </div>
          {form.billing_mode === "slot" && <>
            {!editing && <div><Label className="text-xs">Slot iniziali</Label><Input type="number" min="0" value={form.initial_slots} onChange={(e) => set("initial_slots", e.target.value)} data-testid="client-initial-slots" /></div>}
            <div><Label className="text-xs">Soglia avviso slot in esaurimento</Label><Input type="number" min="0" value={form.slots_low_threshold} onChange={(e) => set("slots_low_threshold", e.target.value)} data-testid="client-threshold" /></div>
          </>}
        </div>
        <div className="flex items-center gap-3"><Switch checked={!!form.active} onCheckedChange={(v) => set("active", v)} data-testid="client-active" /><Label>Cliente attivo</Label></div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={save} className="bg-[#0F2942] hover:bg-[#1B3B5F]" data-testid="client-save">Salva</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CatalogTab({ client, onSaved }) {
  const [courses, setCourses] = useState([]);
  const [sel, setSel] = useState({});
  useEffect(() => { api.get("/courses").then((r) => setCourses(r.data)); }, []);
  useEffect(() => { setSel(Object.fromEntries((client.catalog || []).map((i) => [i.course_id, String(i.price)]))); }, [client]);
  const toggle = (c) => setSel((s) => { const n = { ...s }; if (c.id in n) delete n[c.id]; else n[c.id] = String(c.public_price ?? 0); return n; });
  const save = async () => {
    const items = Object.entries(sel).map(([course_id, price]) => ({ course_id, price: parseFloat(String(price).replace(",", ".")) }));
    if (items.some((i) => Number.isNaN(i.price) || i.price < 0)) return toast.error("Prezzi non validi");
    try { const { data } = await api.put(`/clients/${client.id}/catalog`, { items }); toast.success("Catalogo cliente salvato"); onSaved(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-3">
      <div className="text-sm text-slate-500">Seleziona i corsi visibili al cliente e imposta il prezzo personalizzato (imponibile, IVA del corso aggiunta).</div>
      <div className="border rounded-lg divide-y max-h-[50vh] overflow-y-auto">
        {courses.map((c) => (
          <div key={c.id} className="flex items-center gap-3 p-3" data-testid={`catalog-row-${c.id}`}>
            <Checkbox checked={c.id in sel} onCheckedChange={() => toggle(c)} data-testid={`catalog-check-${c.id}`} />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm truncate">{c.title}</div>
              <div className="text-xs text-slate-500 flex items-center gap-2"><ModalityBadge modality={c.modality} /> Catalogo: {eur(c.public_price)} · IVA {c.vat_rate ?? 22}%</div>
            </div>
            {c.id in sel && (
              <div className="w-32"><Input type="number" step="0.01" min="0" value={sel[c.id]} onChange={(e) => setSel({ ...sel, [c.id]: e.target.value })} data-testid={`catalog-price-${c.id}`} /></div>
            )}
          </div>
        ))}
      </div>
      <div className="flex justify-between items-center"><span className="text-sm text-slate-600">{Object.keys(sel).length} corsi selezionati</span>
        <Button onClick={save} className="bg-[#0F2942] hover:bg-[#1B3B5F]" data-testid="catalog-save">Salva catalogo</Button></div>
    </div>
  );
}

function SlotsTab({ client, onSaved }) {
  const [hist, setHist] = useState([]);
  const [qty, setQty] = useState(10);
  const [notes, setNotes] = useState("");
  const load = useCallback(() => api.get(`/clients/${client.id}/slots/history`).then((r) => setHist(r.data)), [client.id]);
  useEffect(() => { load(); }, [load]);
  const adjust = async (sign) => {
    const n = parseInt(qty, 10);
    if (!n || n <= 0) return toast.error("Inserisci una quantità valida");
    try { const { data } = await api.post(`/clients/${client.id}/slots/adjust`, { delta: sign * n, notes }); toast.success(sign > 0 ? "Slot aggiunti" : "Slot sottratti"); setNotes(""); onSaved(data); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-4">
      {client.billing_mode !== "slot" && <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">Il cliente è in modalità Fatturazione mensile: gli slot non vengono controllati.</div>}
      <div className="flex items-end gap-3 flex-wrap">
        <div><div className="text-xs uppercase tracking-widest font-bold text-slate-500">Slot disponibili</div><div className="font-display font-extrabold text-4xl text-emerald-700" data-testid="admin-slots-balance">{client.slots_balance ?? 0}</div></div>
        <div className="w-24"><Label className="text-xs">Quantità</Label><Input type="number" min="1" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="slots-qty" /></div>
        <div className="flex-1 min-w-[160px]"><Label className="text-xs">Note</Label><Input value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="slots-notes" /></div>
        <Button onClick={() => adjust(1)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="slots-add"><Plus className="w-4 h-4 mr-1" /> Aggiungi</Button>
        <Button onClick={() => adjust(-1)} variant="outline" className="text-red-600" data-testid="slots-subtract"><Minus className="w-4 h-4 mr-1" /> Sottrai</Button>
      </div>
      <div className="font-display font-bold">Storico Slot</div>
      <div className="border rounded-lg max-h-[40vh] overflow-y-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Data</TableHead><TableHead>Operazione</TableHead><TableHead>Quantità</TableHead><TableHead>Saldo</TableHead><TableHead>Utente</TableHead><TableHead>Note</TableHead></TableRow></TableHeader>
          <TableBody>
            {hist.map((t) => (
              <TableRow key={t.id} data-testid={`slot-tx-${t.id}`}>
                <TableCell className="text-xs">{dateIt(t.created_at)}</TableCell>
                <TableCell>{opLabel[t.operation] || t.operation}</TableCell>
                <TableCell className={t.quantity > 0 ? "text-emerald-700 font-semibold" : "text-red-600 font-semibold"}>{t.quantity > 0 ? `+${t.quantity}` : t.quantity}</TableCell>
                <TableCell>{t.balance_after}</TableCell>
                <TableCell className="text-xs">{t.user_email}</TableCell>
                <TableCell className="text-xs">{t.notes}{t.enrollment_number ? ` · ${t.enrollment_number}` : ""}</TableCell>
              </TableRow>
            ))}
            {hist.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-6">Nessun movimento.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function ClientDetailDialog({ clientId, onClose, onChanged }) {
  const [c, setC] = useState(null);
  useEffect(() => { if (clientId) api.get(`/clients/${clientId}`).then((r) => setC(r.data)); else setC(null); }, [clientId]);
  const update = (data) => { setC((x) => ({ ...x, ...data })); onChanged(); };
  return (
    <Dialog open={!!clientId} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="client-detail-dialog">
        <DialogHeader><DialogTitle>Scheda cliente · {c?.company_name}</DialogTitle></DialogHeader>
        {c && (
          <Tabs defaultValue="catalog">
            <TabsList><TabsTrigger value="catalog" data-testid="tab-catalog">Catalogo Cliente</TabsTrigger><TabsTrigger value="slots" data-testid="tab-slots">Slot</TabsTrigger></TabsList>
            <TabsContent value="catalog"><CatalogTab client={c} onSaved={update} /></TabsContent>
            <TabsContent value="slots"><SlotsTab client={c} onSaved={update} /></TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function AdminClients() {
  const [rows, setRows] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [created, setCreated] = useState(null);
  const [pwTarget, setPwTarget] = useState(null);
  const load = useCallback(() => api.get("/clients").then((r) => setRows(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const del = async (c) => {
    if (!window.confirm(`Eliminare il cliente ${c.company_name}? L'accesso verrà rimosso.`)) return;
    await api.delete(`/clients/${c.id}`); toast.success("Cliente eliminato"); load();
  };
  return (
    <div className="space-y-6" data-testid="admin-clients">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold text-slate-900">Clienti</h1>
          <p className="text-slate-500 mt-1">Gestisci i clienti diretti, il loro catalogo personalizzato e gli slot.</p>
        </div>
        <Button onClick={() => { setEditing(null); setFormOpen(true); }} className="bg-[#0F2942] hover:bg-[#1B3B5F]" data-testid="new-client-button"><Plus className="w-4 h-4 mr-1" /> Nuovo cliente</Button>
      </div>
      <Card className="border-slate-200"><CardContent className="p-0 overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Cliente</TableHead><TableHead>Modalità</TableHead><TableHead>Slot</TableHead><TableHead>Corsi</TableHead>
            <TableHead>Iscrizioni</TableHead><TableHead>Stato</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.map((c) => (
              <TableRow key={c.id} data-testid={`client-row-${c.id}`}>
                <TableCell><div className="font-semibold text-slate-900">{c.company_name}</div><div className="text-xs text-slate-500">{c.email}</div></TableCell>
                <TableCell><Badge className={c.billing_mode === "slot" ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-100" : "bg-indigo-100 text-indigo-800 hover:bg-indigo-100"}>{modeLabel(c.billing_mode)}</Badge></TableCell>
                <TableCell className="font-semibold" data-testid={`client-slots-${c.id}`}>{c.billing_mode === "slot" ? c.slots_balance : "—"}</TableCell>
                <TableCell>{(c.catalog || []).length}</TableCell>
                <TableCell>{c.enrollments_count}</TableCell>
                <TableCell>{c.active ? <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Attivo</Badge> : <Badge className="bg-slate-100 text-slate-600 hover:bg-slate-100">Disattivo</Badge>}</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button variant="outline" size="sm" onClick={() => setDetailId(c.id)} data-testid={`client-manage-${c.id}`}><Settings2 className="w-4 h-4 mr-1" /> Catalogo e slot</Button>
                  <Button variant="ghost" size="icon" onClick={() => { setEditing(c); setFormOpen(true); }} data-testid={`client-edit-${c.id}`}><Pencil className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="icon" title="Cambia password" onClick={() => setPwTarget({ type: "cliente", id: c.id, name: c.company_name, email: c.email })} data-testid={`client-password-${c.id}`}><KeyRound className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => del(c)} data-testid={`client-delete-${c.id}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-10">Nessun cliente creato.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </CardContent></Card>
      <ClientFormDialog open={formOpen} editing={editing} onClose={() => setFormOpen(false)}
        onSaved={(data) => { setFormOpen(false); load(); if (data) setCreated(data); }} />
      <ClientDetailDialog clientId={detailId} onClose={() => setDetailId(null)} onChanged={load} />
      <ResetPasswordDialog target={pwTarget} onClose={() => setPwTarget(null)} />
      <Dialog open={!!created} onOpenChange={(v) => { if (!v) setCreated(null); }}>
        <DialogContent className="max-w-md" data-testid="client-created-dialog">
          <DialogHeader><DialogTitle>Cliente creato</DialogTitle></DialogHeader>
          <div className="text-sm space-y-2">
            <div>Comunica al cliente le credenziali di accesso:</div>
            <div>Email: <strong>{created?.client?.email}</strong></div>
            <div>Password: <strong className="font-mono" data-testid="client-created-password">{created?.temporary_password}</strong></div>
          </div>
          <DialogFooter><Button onClick={() => { setDetailId(created.client.id); setCreated(null); }} data-testid="client-created-open-catalog">Configura catalogo</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
